/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IMessageBarProps, MessageBar, MessageBarType } from '@fluentui/react/lib/MessageBar';
import * as React from 'react';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { globalMessages } from 'src/core/utils/globalMessages';

export type LimitedAccessToFeaturesMessageBarProps = IMessageBarProps & {
    /**
     * The user role to display in the message bar
     */
    displayedUserRole: string;

    /**
     * The message to show inside the message bar. If not provided,
     * `globalMessages.cannotAccessFeaturesOrPerformActions` is used.
     */
    message?: string;
};

/**
 * Renders a message bar that shows that the user's current
 * role has limited access to features and options in this page
 *
 * It differs from the common tool tip is that
 * @param props The input properties for the MessageBar.
 */

export const LimitedAccessToFeaturesMessageBar = (props: LimitedAccessToFeaturesMessageBarProps) => {
    const { displayedUserRole, message, ...messageBarProps } = props;
    const { localize } = useLocalization();

    return (
        <MessageBar messageBarType={MessageBarType.warning} {...messageBarProps}>
            {message ?? localize(globalMessages.cannotAccessFeaturesOrPerformActions, { displayedUserRole })}
            &nbsp;
            <ExternalLink href="https://aka.ms/LUIS-RBAC">{localize(globalMessages.learnMoreAboutAllowedFeature)}</ExternalLink>
        </MessageBar>
    );
};
